import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import { motion, AnimatePresence } from 'framer-motion';
import FormModal from '../components/common/FormModal';

const { 
  FiSend, FiSearch, FiMoreVertical, FiUser, FiCheck, FiCheckCircle, 
  FiClock, FiPaperclip, FiImage, FiFileText, FiInfo, FiStar, 
  FiZap, FiTrash2, FiPlus, FiDownload, FiX, FiEdit2, FiTag
} = FiIcons;

export default function Messages() {
  const { addToast } = useToast();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfile, setShowProfile] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Template Management
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchClients();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchMessages(selectedClient.id);
      markAsRead(selectedClient.id);

      const channel = supabase
        .channel(`messages:${selectedClient.id}`)
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'messages_2024', 
          filter: `client_id=eq.${selectedClient.id}` 
        }, payload => {
          setMessages(prev => [...prev, payload.new]);
          if (payload.new.sender_type === 'client') markAsRead(selectedClient.id);
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [selectedClient]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase.from('clients_2024').select('*').order('name');
      if (error) throw error;
      setClients(data || []);
      if (data?.length > 0) setSelectedClient(data[0]);
    } catch (error) { addToast(error.message, 'error'); } 
    finally { setLoading(false); }
  };

  const fetchTemplates = async () => {
    const { data } = await supabase.from('message_templates_2024').select('*').order('created_at', { ascending: false });
    setTemplates(data || []);
  };

  const fetchMessages = async (clientId) => {
    const { data } = await supabase.from('messages_2024')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true });
    setMessages(data || []);
  };

  const markAsRead = async (clientId) => {
    await supabase.from('messages_2024')
      .update({ read_at: new Date() })
      .eq('client_id', clientId)
      .eq('sender_type', 'client')
      .is('read_at', null);
  };

  const substituteVariables = (text, client) => {
    if (!client) return text;
    return text
      .replace(/{client_name}/g, client.name)
      .replace(/{program}/g, client.program || 'your program')
      .replace(/{coach_name}/g, 'Coach');
  };

  const handleSendMessage = async (e, content = newMessage, fileData = null) => {
    if (e) e.preventDefault();
    const finalContent = substituteVariables(content, selectedClient);
    if (!finalContent.trim() && !fileData) return;
    setSending(true);

    try {
      const payload = {
        client_id: selectedClient.id,
        content: finalContent,
        sender_type: 'coach',
        ...fileData
      };
      const { error } = await supabase.from('messages_2024').insert([payload]);
      if (error) throw error;
      setNewMessage('');
      setShowTemplates(false);
    } catch (error) { addToast(error.message, 'error'); } 
    finally { setSending(false); }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setTemplateLoading(true);
    const formData = new FormData(e.target);
    const payload = {
      title: formData.get('title'),
      content: formData.get('content'),
      category: formData.get('category'),
      is_automated: formData.get('is_automated') === 'on'
    };

    try {
      if (editingTemplate) {
        const { error } = await supabase.from('message_templates_2024').update(payload).eq('id', editingTemplate.id);
        if (error) throw error;
        addToast("Template updated");
      } else {
        const { error } = await supabase.from('message_templates_2024').insert([payload]);
        if (error) throw error;
        addToast("New template created");
      }
      setIsTemplateModalOpen(false);
      fetchTemplates();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setTemplateLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!confirm("Delete this template?")) return;
    await supabase.from('message_templates_2024').delete().eq('id', id);
    fetchTemplates();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    addToast("Uploading file...", "info");
    const mockFile = {
      file_url: 'https://images.unsplash.com/photo-1454165833767-027eeef1593e?w=800',
      file_name: file.name,
      file_type: file.type
    };
    handleSendMessage(null, `Shared a file: ${file.name}`, mockFile);
  };

  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>;

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6">
      {/* Sidebar - Conversations */}
      <div className="w-80 bg-slate-900 border border-slate-800 rounded-[32px] flex flex-col overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-slate-800 bg-slate-800/20">
          <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-tight">Messages</h2>
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
            <input 
              type="text" 
              placeholder="Search students..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800 border-none rounded-xl pl-9 pr-4 py-2.5 text-xs text-white focus:ring-2 focus:ring-indigo-500 outline-none" 
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {filteredClients.map(client => (
            <button 
              key={client.id} 
              onClick={() => setSelectedClient(client)}
              className={`w-full p-5 flex items-center gap-4 transition-all border-b border-slate-800/50 ${selectedClient?.id === client.id ? 'bg-indigo-600/10 border-l-4 border-l-indigo-500' : 'hover:bg-slate-800/50'}`}
            >
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center text-indigo-400 font-bold text-sm shrink-0 border border-slate-700">
                  {client.name.charAt(0)}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-slate-900 rounded-full" />
              </div>
              <div className="text-left overflow-hidden flex-1">
                <p className={`text-sm font-bold truncate ${selectedClient?.id === client.id ? 'text-indigo-400' : 'text-white'}`}>{client.name}</p>
                <p className="text-[10px] text-slate-500 truncate uppercase tracking-widest font-semibold">{client.program || 'General Coaching'}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`flex-1 bg-slate-900 border border-slate-800 rounded-[32px] flex flex-col overflow-hidden shadow-2xl relative`}>
        {selectedClient ? (
          <>
            {/* Header */}
            <div className="px-8 py-5 border-b border-slate-800 flex justify-between items-center bg-slate-800/40 backdrop-blur-md sticky top-0 z-10">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-bold">
                  {selectedClient.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-white font-bold">{selectedClient.name}</h3>
                  <div className="flex items-center gap-1.5 text-[9px] text-emerald-400 font-bold uppercase tracking-widest">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Session
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowProfile(!showProfile)}
                  className={`p-2.5 rounded-xl transition-all ${showProfile ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-white hover:bg-slate-800'}`}
                >
                  <SafeIcon icon={FiInfo} />
                </button>
                <button className="p-2.5 text-slate-500 hover:text-white rounded-xl hover:bg-slate-800 transition-all">
                  <SafeIcon icon={FiMoreVertical} />
                </button>
              </div>
            </div>

            {/* Messages List */}
            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar"
            >
              {messages.map((msg, idx) => {
                const isCoach = msg.sender_type === 'coach';
                return (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={msg.id || idx} 
                    className={`flex ${isCoach ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] group`}>
                      <div className={`relative px-5 py-3 rounded-2xl shadow-xl ${isCoach ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 rounded-tl-none border border-slate-700/50'}`}>
                        {msg.file_url ? (
                          <div className="mb-2 space-y-3">
                            {msg.file_type?.startsWith('image/') ? (
                              <img src={msg.file_url} alt="Shared" className="rounded-lg max-h-60 w-full object-cover border border-white/10" />
                            ) : (
                              <div className="flex items-center gap-3 bg-black/20 p-3 rounded-xl border border-white/5">
                                <SafeIcon icon={FiFileText} className="text-2xl text-indigo-300" />
                                <div className="flex-1 overflow-hidden">
                                  <p className="text-xs font-bold truncate">{msg.file_name}</p>
                                  <p className="text-[9px] opacity-60 uppercase tracking-widest">Document Shared</p>
                                </div>
                                <a href={msg.file_url} className="p-2 hover:bg-white/10 rounded-lg transition-colors"><SafeIcon icon={FiDownload} /></a>
                              </div>
                            )}
                            <p className="text-sm leading-relaxed">{msg.content}</p>
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed">{msg.content}</p>
                        )}
                        
                        <div className={`flex items-center gap-2 mt-2 ${isCoach ? 'justify-end' : 'justify-start'}`}>
                          <span className="text-[8px] opacity-50 font-bold uppercase tracking-widest">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isCoach && (
                            <SafeIcon icon={msg.read_at ? FiCheckCircle : FiCheck} className={`text-[10px] ${msg.read_at ? 'text-indigo-200' : 'text-indigo-400 opacity-50'}`} />
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Quick Templates Bar */}
            <AnimatePresence>
              {showTemplates && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute bottom-24 left-8 right-8 bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl p-6 z-20"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <SafeIcon icon={FiZap} className="text-indigo-400" /> Automated Templates
                    </h4>
                    <button onClick={() => setShowTemplates(false)} className="text-slate-500 hover:text-white"><SafeIcon icon={FiX} /></button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 overflow-y-auto max-h-48 pr-2 custom-scrollbar">
                    {templates.map(t => (
                      <div key={t.id} className="group relative">
                        <button 
                          onClick={() => handleSendMessage(null, t.content)}
                          className="w-full text-left p-4 bg-slate-800 hover:bg-indigo-600 rounded-2xl transition-all border border-slate-700 group-hover:border-indigo-400 shadow-lg"
                        >
                          <p className="text-xs font-bold text-white mb-1">{t.title}</p>
                          <p className="text-[10px] text-slate-400 group-hover:text-indigo-100 line-clamp-1">{t.content}</p>
                        </button>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); setEditingTemplate(t); setIsTemplateModalOpen(true); }} className="p-1.5 bg-black/40 text-white rounded-lg hover:bg-black/60"><SafeIcon icon={FiEdit2} className="text-[10px]" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }} className="p-1.5 bg-rose-500/20 text-rose-400 rounded-lg hover:bg-rose-500/40"><SafeIcon icon={FiTrash2} className="text-[10px]" /></button>
                        </div>
                      </div>
                    ))}
                    <button 
                      onClick={() => { setEditingTemplate(null); setIsTemplateModalOpen(true); }}
                      className="p-4 bg-indigo-600/5 text-indigo-400 border border-dashed border-indigo-500/30 rounded-2xl text-xs font-bold flex flex-col items-center justify-center gap-2 hover:bg-indigo-600/10 transition-all"
                    >
                      <SafeIcon icon={FiPlus} className="text-lg" /> Create New
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area */}
            <div className="p-6 border-t border-slate-800 bg-slate-950/50 backdrop-blur-sm">
              <form onSubmit={handleSendMessage} className="flex items-center gap-3">
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                <button 
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="w-12 h-12 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-2xl flex items-center justify-center transition-all border border-slate-700"
                >
                  <SafeIcon icon={FiPaperclip} className="text-lg" />
                </button>
                <div className="flex-1 relative">
                  <input 
                    type="text" 
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Message your student..." 
                    className="w-full bg-slate-800 border-none rounded-2xl pl-6 pr-14 py-4 text-white focus:ring-2 focus:ring-indigo-500 outline-none shadow-inner" 
                  />
                  <button 
                    type="button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${showTemplates ? 'text-indigo-400' : 'text-slate-500 hover:text-indigo-400'}`}
                  >
                    <SafeIcon icon={FiZap} />
                  </button>
                </div>
                <button 
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-2xl flex items-center justify-center transition-all shadow-lg shadow-indigo-600/20"
                >
                  <SafeIcon icon={FiSend} className={sending ? 'animate-pulse' : ''} />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
            <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center mb-6 border border-indigo-500/20">
              <SafeIcon icon={FiUser} className="text-4xl text-indigo-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Select a Conversation</h3>
            <p className="text-slate-400 max-w-sm">Choose a student to start a high-performance coaching conversation.</p>
          </div>
        )}
      </div>

      {/* Profile Side Panel */}
      <AnimatePresence>
        {selectedClient && showProfile && (
          <motion.aside 
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="bg-slate-900 border border-slate-800 rounded-[32px] overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="p-8 text-center border-b border-slate-800">
              <div className="w-20 h-20 rounded-3xl bg-indigo-600 mx-auto mb-4 flex items-center justify-center text-3xl font-extrabold text-white shadow-xl shadow-indigo-600/20">
                {selectedClient.name.charAt(0)}
              </div>
              <h3 className="text-lg font-bold text-white">{selectedClient.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{selectedClient.email}</p>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
               <div>
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Key Metrics</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                      <p className="text-[9px] text-slate-500 font-bold uppercase">Progress</p>
                      <p className="text-sm font-black text-white">84%</p>
                    </div>
                    <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                      <p className="text-[9px] text-slate-500 font-bold uppercase">Engagement</p>
                      <p className="text-sm font-black text-emerald-400">High</p>
                    </div>
                  </div>
               </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Template Editor Modal */}
      <FormModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        title={editingTemplate ? "Edit Template" : "New Message Template"}
        onSubmit={handleSaveTemplate}
        loading={templateLoading}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Template Title</label>
            <input name="title" defaultValue={editingTemplate?.title} required className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-sm text-white outline-none" placeholder="e.g. Weekly Check-in" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Category</label>
              <select name="category" defaultValue={editingTemplate?.category} className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-sm text-white outline-none">
                <option value="General">General</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Feedback">Feedback</option>
                <option value="Correction">Correction</option>
              </select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input type="checkbox" name="is_automated" defaultChecked={editingTemplate?.is_automated} className="w-5 h-5 rounded bg-slate-800 border-slate-700 text-indigo-600 focus:ring-indigo-500" />
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Enable for Automation</label>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex justify-between">
              Content
              <span className="text-indigo-400 font-medium normal-case">Use {"{client_name}"}, {"{program}"}</span>
            </label>
            <textarea name="content" defaultValue={editingTemplate?.content} rows="5" required className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-sm text-white outline-none resize-none" placeholder="Write your template here..."></textarea>
          </div>
        </div>
      </FormModal>
    </div>
  );
}